/**
 * «Guardar en la cabecera» (F4 de «Personalizar informes», 29-09-2026): lo que
 * hace el botón de la tarjeta `marca_cambio` del asistente.
 *
 * La edge NO escribe (como en el cuaderno, A20): propone UN dato ya validado y lo
 * guarda el navegador con la sesión del ADMIN (la RLS de `company_report_branding`
 * solo le deja a él). Se escribe SOLO esa columna: `visibles` no se toca, así que
 * guardar el NIF para un informe no lo enciende en todos (eso se decide en
 * Ajustes). La primera vez crea la fila con lo de por defecto (cabecera limpia).
 *
 * La tarjeta se vuelve a comprobar ENTERA antes de escribir (`leerTarjetaMarca`):
 * una del historial con el NIF tapado (E11) o trucada no se guarda.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import { checkNif, detalleNif, FORMATO_NIF } from "@/lib/cuaderno/validators";
import { normalizarDictado } from "@/lib/cuaderno/dictado";
import { leerTarjetaMarca, type ReglasNif, type TarjetaMarca } from "./reglasCabecera";
import { olvidarMarca } from "./cargarMarca";

/** Las reglas del NIF del cuaderno, en el navegador (las mismas que la copia Deno). */
export const NIF_NAVEGADOR: ReglasNif = { check: checkNif, detalle: detalleNif, dictado: normalizarDictado, formato: FORMATO_NIF };

/** La tarjeta, comprobada; null si no se puede guardar. PURA. */
export const tarjetaMarca = (item: unknown): TarjetaMarca | null => leerTarjetaMarca(item, NIF_NAVEGADOR);

export type ResultadoGuardar = { ok: true } | { ok: false; motivo: "invalida" | "permiso" | "error" };

/** Guarda el dato de la tarjeta en la cabecera de la empresa. Nunca lanza. */
export async function guardarDatoCabecera(
  sb: Pick<SupabaseClient, "from">,
  companyId: string,
  item: unknown,
): Promise<ResultadoGuardar> {
  const t = tarjetaMarca(item);
  if (!t) return { ok: false, motivo: "invalida" };
  try {
    const { error } = await sb
      .from("company_report_branding" as never)
      .upsert({ company_id: companyId, [t.campo]: t.valor } as never, { onConflict: "company_id" });
    if (error) {
      // 42501: la RLS (no es admin de esta empresa).
      return { ok: false, motivo: (error as { code?: string }).code === "42501" ? "permiso" : "error" };
    }
    // El próximo informe pregunta otra vez: ya sale con el dato.
    olvidarMarca(companyId);
    return { ok: true };
  } catch {
    return { ok: false, motivo: "error" };
  }
}
